import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportFatal, serializeError } from '@/lib/errorReport';
import { ErrorFallback } from './ErrorFallback';

type Props = { children: ReactNode };
type State = { hasError: boolean };

/**
 * ErrorBoundary global do admin: reporta crash de render como FATAL e mostra
 * o `ErrorFallback` (recarregar).
 *
 * Origem: #12 — commit ededea11d, 2026-06-16 (§3.4.3, ErrorBoundary global +
 * PostHog `capture_exceptions`).
 *
 * Por quê: sem ele, qualquer exceção no render derruba a árvore inteira para
 * uma tela branca, SEM nenhum evento — em prod não temos DevTools do cliente.
 *
 * Contrato: `componentDidCatch` chama `reportFatal('admin_render_crash', …)`
 * com o `componentStack` cortado em 1.500 caracteres + `serializeError`.
 * Depois do erro, renderiza só o fallback até recarregar.
 *
 * Usado por: `main.tsx` (envolve `AppVersionGate`, `InstallPrompt`,
 * `WebPushBootstrap` e o `RouterProvider`). Só web `apps/admin`.
 *
 * Arquitetura: fica POR FORA do `RouterProvider`, então NÃO recebe erro de
 * render das rotas — o data router do React Router 7 os captura antes. Esses
 * vão pelo `RouteErrorBoundary` (`admin_route_crash`, #795 OBS-3).
 *
 * Dados — evento PostHog `admin_render_crash` (via `reportFatal`):
 *   { "severity": "FATAL", "componentStack": "…(≤1500)",
 *     "errorName": "TypeError", "errorMessage": "…", "errorStack": "…" }
 *
 * Falhas conhecidas:
 *  - 2026-10-03 (#795, OBS-3): crash de tela nunca emitia este evento porque
 *    o boundary fica fora do router; corrigido com o `RouteErrorBoundary`,
 *    não movendo este.
 *
 * Issues: #12 (§3.4.3), #795 (OBS-3).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportFatal('admin_render_crash', {
      componentStack: info.componentStack?.slice(0, 1500) ?? null,
      ...serializeError(error),
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) return <ErrorFallback />;
    return this.props.children;
  }
}
